import React, { useState, useEffect } from 'react'
import axiosInstance from '../../services/api/axios'
import whatsappBg from '../../assets/images/whatsapp-bg.jpg'
import { CloseButton, OutlineButton, PrimaryButton } from '../../components/Buttons'

function WhatsappTemplates({projectId}) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showModal, setShowModal] = useState(false)
console.log(projectId);
  useEffect(() => {
    if (projectId) {
      fetchTemplates()
    }
  }, [projectId])

  const fetchTemplates = async () => {
    setLoading(true)
    setError(null)
    try {
      // Manually call the GET /api/whatsapp/templates endpoint with projectId as query param
      const response = await axiosInstance.get('/whatsapp/templates', {
        params: { projectId }
      });
      
      // Handle both paginated and non-paginated responses
      const templatesArray = response?.items || (Array.isArray(response) ? response : []);
      
      // Check if response is empty or has no templates
      if (templatesArray.length === 0) {
        setTemplates([]);
        return;
      }
      
      setTemplates(templatesArray);
    } catch (err) {
      // Check for specific 404 error about WhatsApp business account
      const errorMessage = err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Failed to fetch WhatsApp templates';
      
      if (err?.response?.status === 404 && 
          (errorMessage.includes('Whatsapp business account') || 
           errorMessage.includes('secret access token') ||
           errorMessage.includes('business account id'))) {
        setError('WhatsApp Business Account is not configured. Please configure your WhatsApp Business Account ID and Secret Access Token to use templates.');
      } else {
        setError(errorMessage);
      }
      
      console.error('Error fetching WhatsApp templates:', err);
      setTemplates([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }

  const createTemplates = async () => {
    setLoading(true)
    setError(null)
    try {
      // Frontend makes ONE call to create templates
      const response = await axiosInstance.post('/whatsapp/templates/create', null, {
        params: { project_id: projectId }
      });

      // After creating templates, fetch the updated list
      await fetchTemplates();
      // Show success message (you could add a toast notification here)
      console.log('Templates created successfully:', response);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to create WhatsApp templates');
      console.error('Error creating WhatsApp templates:', err);
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800'
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const openModal = (template) => {
    setSelectedTemplate(template)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedTemplate(null)
  }

  const renderWhatsAppMessage = (template) => {
    if (!template.components || template.components.length === 0) {
      return (
        <div className="text-gray-600 italic text-sm">No message content available</div>
      )
    }

    return template.components.map((component, index) => {
      switch (component.type) {
        case 'HEADER':
          // Check if it's an image header
          if (component.format === 'IMAGE' && component.example?.header_handle?.[0]) {
            return (
              <div key={index} className="mb-3">
                <div className="w-full mb-2 rounded-md overflow-hidden">
                  <img
                    src={component.example.header_handle[0]}
                    alt="Header"
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      console.error('Header image failed to load:', e);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )
          }
          // Regular text header
          return (
            <div key={index} className="mb-3">
              <div className="text-gray-900 font-semibold text-lg leading-tight">
                {component.text || component.format || 'Header content'}
              </div>
            </div>
          )
        case 'BODY':
          return (
            <div key={index} className="mb-3">
              <div className="text-gray-800 whitespace-pre-wrap leading-relaxed text-sm">
                {component.text || 'Body content with parameters'}
              </div>
            </div>
          )
        case 'FOOTER':
          return (
            <div key={index} className="mb-3">
              <div className="text-gray-500 text-xs">
                {component.text || 'Footer content'}
              </div>
            </div>
          )
        case 'BUTTONS':
          return (
            <div key={index} className="mt-4">
              <div className="space-y-2">
                {component.buttons?.map((button, btnIndex) => (
                  <OutlineButton
                    key={btnIndex}
                    className="w-full px-4 py-2 rounded text-sm font-medium transition-colors"
                  >
                    {button.text || `Button ${btnIndex + 1}`}
                  </OutlineButton>
                )) || (
                  <div className="text-gray-500 text-xs italic">No buttons defined</div>
                )}
              </div>
            </div>
          )
        default:
          return (
            <div key={index} className="mb-3">
              <div className="text-gray-800 text-sm">
                {component.text || 'Component content'}
              </div>
            </div>
          )
      }
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Loading WhatsApp Templates</h3>
                <p className="text-gray-600">Please wait while we fetch your templates...</p>
              </div>
            </div>
          </div>
          
          {/* Loading skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    // Check if it's a WhatsApp business account configuration error
    const isConfigError = error.includes('WhatsApp Business Account') || 
                         error.includes('business account') || 
                         error.includes('secret access token');
    
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className={`bg-white rounded-xl shadow-sm border p-8 ${isConfigError ? 'border-yellow-200' : 'border-red-200'}`}>
            <div className="text-center">
              <div className={`p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center ${isConfigError ? 'bg-yellow-100' : 'bg-red-100'}`}>
                {isConfigError ? (
                  <svg className="h-10 w-10 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-10 w-10 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isConfigError ? 'WhatsApp Business Account Not Configured' : 'Error Loading Templates'}
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
              {isConfigError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                  <p className="text-sm text-yellow-800">
                    To use WhatsApp templates, you need to configure your WhatsApp Business Account ID and Secret Access Token in the system settings.
                  </p>
                </div>
              )}
             
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!projectId) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-8">
            <div className="text-center">
              <div className="p-4 bg-yellow-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg className="h-10 w-10 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Project Selected</h3>
              <p className="text-gray-600 max-w-md mx-auto">Please select a project to view and manage WhatsApp templates.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen space-y-4">
      <div className="w-full space-y-4">
        {/* Header card */}
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-4 lg:p-5">
          <div>
            <h1 className="text-base lg:text-lg font-semibold text-slate-900">WhatsApp Templates</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Manage WhatsApp message templates for project onboarding.
            </p>
          </div>
        </div>

        {/* Stats — small slate cards with a coloured accent dot per status */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: "Total Templates", value: templates.length, dot: "bg-slate-400" },
            { label: "Approved", value: templates.filter((t) => t.status === "APPROVED").length, dot: "bg-emerald-500" },
            { label: "Pending", value: templates.filter((t) => t.status === "PENDING").length, dot: "bg-flamingo-500" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                <span className={`w-2 h-2 rounded-full ${stat.dot}`} />
                {stat.label}
              </div>
              <p className="text-2xl font-semibold text-slate-900 mt-2">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className=" text-center">
            <div className="max-w-md mx-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-500 mb-6">No WhatsApp templates are available for this project.</p>
              <button
                onClick={createTemplates}
                disabled={loading || !projectId}
                className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Create Your First Template
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all duration-200 hover:border-flamingo-200 hover:-translate-y-0.5 group">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <svg className="h-4 w-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <h3 className="text-base font-semibold text-slate-900 truncate group-hover:text-flamingo-700 transition-colors">{template.name}</h3>
                    </div>
                    <p className="text-xs text-slate-500 truncate">ID: {template.id}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${getStatusColor(template.status)}`}>
                    {template.status}
                  </span>
                </div>

                {/* Template Details */}
                <div className="space-y-3 mb-6">
                  {template.category && (
                    <div className="flex items-center space-x-2">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-sm text-gray-600">{template.category}</span>
                    </div>
                  )}

                  {template.language && (
                    <div className="flex items-center space-x-2">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      <span className="text-sm text-gray-600">{template.language}</span>
                    </div>
                  )}

                  {template.components && template.components.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm text-gray-500">{template.components.length} components</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-100">
                  <PrimaryButton 
                    onClick={() => openModal(template)}
                    className="w-full  px-4 py-2.5 "
                  >
                    View Details
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* WhatsApp Preview Modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 sm:p-6 lg:p-8 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-auto max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedTemplate.name}</h2>
                  <p className="text-sm text-gray-500 mt-1">Template Preview</p>
                </div>
                <CloseButton  onClick={closeModal} />
                
              </div>
            </div>

            {/* WhatsApp Message Preview */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div 
                className="rounded overflow-hidden relative flex justify-end p-4"
                style={{
                  backgroundImage: `url(${whatsappBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  minHeight: '200px'
                }}
              >
                <div className="bg-white p-4 rounded-lg shadow-sm max-w-[85%] relative min-h-[100px] hover:shadow-md transition-shadow duration-200 border border-gray-100">
                  {/* WhatsApp message header with business name */}
                  <div className="flex items-center text-[11px] text-base text-green-600 font-medium mb-3">
                    <span className="text-blue-950">Build<span className="text-orange-500">Vizer</span></span>
                  </div>
                  
                  {/* Message content */}
                  <div className="space-y-3">
                    {renderWhatsAppMessage(selectedTemplate)}
                  </div>
                  
                  {/* Timestamp */}
                  <div className="flex justify-end mt-4">
                    <span className="text-[10px] text-gray-500">14:44</span>
                  </div>
                </div>
              </div>
            </div>

           
          </div>
        </div>
      )}
    </div>
  )
}

export default WhatsappTemplates
